/**
 * Calls to the StitchCraft server (same origin). Errors come back as stable codes, translated here.
 * When the app is served without the server (static hosting, Vite without `dev:server`), accounts
 * are simply unavailable.
 */
import { t } from '../i18n/i18n';

export type Role = 'user' | 'admin' | 'superadmin';

export interface AccountUser {
  id: number;
  email: string;
  role: Role;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    /** Server detail (only for admins, e.g. why Brevo refused an email). */
    public detail?: string,
  ) {
    super(errorMessage(code, detail));
  }
}

/** Header the server requires on state-changing calls (protection against cross-site requests). */
const CSRF = { 'X-StitchCraft': '1' };

export async function api<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: { ...(method === 'GET' ? {} : CSRF), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('network', 0);
  }
  const data = (await res.json().catch(() => null)) as (T & { error?: string; message?: string }) | null;
  if (!res.ok) throw new ApiError(data?.error ?? 'server_error', res.status, data?.message);
  return data as T;
}

/** null when the app runs without the server. */
export async function fetchMe(): Promise<{ accounts: boolean; user: AccountUser | null } | null> {
  try {
    const res = await fetch('/api/me', { credentials: 'same-origin' });
    if (!res.ok || !(res.headers.get('content-type') ?? '').includes('application/json')) return null;
    return (await res.json()) as { accounts: boolean; user: AccountUser | null };
  } catch {
    return null;
  }
}

export function errorMessage(code: string, detail?: string): string {
  switch (code) {
    case 'invalid_email':
      return t('Enter a valid email address.');
    case 'weak_password':
      return t('The password must be at least {n} characters.', { n: 10 });
    case 'invalid_credentials':
      return t('Wrong email or password.');
    case 'email_not_verified':
      return t('Confirm your email address first. Enter the code we sent you.');
    case 'blocked':
      return t('This account is blocked. Contact the administrator.');
    case 'invalid_code':
      return t('The code is not correct.');
    case 'code_expired':
      return t('The code has expired. Ask for a new code.');
    case 'too_many_attempts':
      return t('Too many wrong attempts. Ask for a new code.');
    case 'rate_limited':
      return t('Too many attempts. Wait a little and try again.');
    case 'unauthorized':
      return t('You are not logged in.');
    case 'forbidden':
      return t('You do not have access to this.');
    case 'not_found':
      return t('Not found.');
    case 'too_large':
      return t('The project is too large to store in the cloud.');
    case 'project_limit':
      return t('You have reached the maximum number of cloud projects. Delete some first.');
    case 'invalid_project':
      return t('The project could not be stored.');
    case 'mail_failed':
      return detail ? t('The email could not be sent: {detail}', { detail }) : t('The email could not be sent. Try again later or contact the administrator.');
    case 'accounts_disabled':
      return t('Accounts are not available on this server.');
    case 'network':
      return t('No connection to the server.');
    default:
      return t('Something went wrong on the server. Try again.');
  }
}
